import React from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { cancelRequest } from "store/actions/commonActions";
import { loginAsync } from "store/actions/authActions";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { LoginViewProps } from "models/propTypes/loginPropTypes";
import { IconDefinitionConstants } from "utils/constants";
import { AuthEnums, StyleEnums } from "models/enums";
//#endregion
//#region components
import {
  ActionLinkButton,
  BankIdLoading,
  BankIdQRCode,
  Icon,
  StaticInfoMessage,
  StyledButton,
  useMountedState,
} from "components/common";
import { Grid } from "@mui/material";
import useLoginContext from "./context/useLoginContext";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";

const BankIdAppLogin: React.FC<LoginViewProps> = (props) => {
  const [isSubmitting, setSubmitting] = React.useState<boolean>(false);
  const [currentRequest, setCurrentRequest] = React.useState<string>();
  const [qrCode, setQrCode] = React.useState<string>();

  const { setLoginView } = useLoginContext();

  const toggleLoginForm = () => {
    setLoginView(AuthEnums.LoginView.UserNamePassword);
  };

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const { getMounted } = useMountedState();

  const qrCodeCallback = (qrStartCode: string, time: string, qrAuthCode: string) => {
    setQrCode(`bankid.${qrStartCode}.${time}.${qrAuthCode}`);
  };

  const onOpenMobileAppClick = () => {
    setSubmitting(true);
    const requestId = `BANKID_QRCODE_${UtilsHelper.getUniqueId()}`;
    setCurrentRequest(requestId);

    (
      dispatch(
        loginAsync(requestId, undefined, AuthEnums.LoginMethod.QR_CODE, qrCodeCallback)
      ) as unknown as Promise<number>
    ).finally(() => {
      if (getMounted()) {
        setSubmitting(false);
        setCurrentRequest(undefined);
        setQrCode(undefined);
      }
    });
  };

  const onOpenBankIdClick = () => {
    setSubmitting(true);
    const requestId = `BANKID_LOGIN_${UtilsHelper.getUniqueId()}`;
    setCurrentRequest(requestId);

    (
      dispatch(loginAsync(requestId, undefined, AuthEnums.LoginMethod.BANKID_DESKTOP)) as unknown as Promise<number>
    ).finally(() => {
      if (getMounted()) {
        setSubmitting(false);
        setCurrentRequest(undefined);
      }
    });
  };

  const cancelLogin = () => {
    if (currentRequest && !props.isAuthenticated) {
      dispatch(cancelRequest(currentRequest));
      setSubmitting(false);
      setCurrentRequest(undefined);
      setQrCode(undefined);
    }
  };

  // Problem with opening BankID on desktop, so that button will be hidden for non-mobile devices.
  // Detecting if touchscreen should be "good enough".
  const isTouchDevice = "ontouchstart" in window;

  const logInProblems = false;

  return (
    <>
      <Grid container spacing={4} style={isSubmitting ? { height: 0, visibility: "hidden" } : undefined}>
        <Grid container item>
          <Grid container spacing={1}>
            <Grid item container className={cssExports.loginHeader}>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Login.Login)}
            </Grid>
            <Grid item container className={cssExports.loginMessage}>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Login.LoginMessage)}
            </Grid>
          </Grid>
        </Grid>
        <Grid item container justifyContent="flex-start" spacing={2}>
          {logInProblems && (
            <Grid item container>
              <StaticInfoMessage displayType={StyleEnums.DisplayType.Warning}>
                Just nu har vi tekniska problem som kan påverka inloggningen. Vi arbetar aktivt på att lösa detta så
                snabbt som möjligt. Tack för ditt tålamod!
              </StaticInfoMessage>
            </Grid>
          )}
          <Grid item container>
            <StyledButton
              onClick={onOpenMobileAppClick}
              className={cssExports.loginButton}
              testId="openMobileBankIdButton"
            >
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Login.OpenMobileBankId)}
            </StyledButton>
          </Grid>
          <Grid item container>
            {isTouchDevice ? (
              <StyledButton
                onClick={onOpenBankIdClick}
                className={cssExports.loginButton}
                secondary
                testId="openBankIdButton"
              >
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Login.OpenBankId)}
              </StyledButton>
            ) : (
              <></>
            )}
          </Grid>
        </Grid>
      </Grid>

      {isSubmitting &&
        (qrCode ? (
          <BankIdQRCode
            qrCodeData={qrCode}
            cancelLoadingText={TranslationKeys.Login.CancelLogin}
            onLoadingCancelHandler={cancelLogin}
          />
        ) : (
          <BankIdLoading
            customText=""
            cancelLoadingText={TranslationKeys.Login.CancelLogin}
            onLoadingCancelHandler={cancelLogin}
          />
        ))}

      {!isSubmitting && (
        <Grid container className={cssExports.bottomContent}>
          <ActionLinkButton
            onClick={toggleLoginForm}
            endIcon={<Icon icon={IconDefinitionConstants.WArrowSmallRight} />}
            testId="loginWithPasswordButton"
          >
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Login.LoginWithPassword)}
          </ActionLinkButton>
        </Grid>
      )}
    </>
  );
};

export default BankIdAppLogin;
